import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { farmerApi, offerStatusLabel, type Buyable, type FarmerProfile, type Offer } from '../lib/farmer'
import { money, num, shortDate } from '../lib/format'
import { Banner, Empty, Loading, Panel, Pill, StatMini } from '../ui/kit'
import { FarmerHeader } from './FarmerShell'

export function FarmerHome() {
  const [me, setMe] = useState<FarmerProfile | null>(null)
  const [buy, setBuy] = useState<Buyable[]>([])
  const [offers, setOffers] = useState<Offer[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      farmerApi.get<FarmerProfile>('/portal/me'),
      farmerApi.get<Buyable[]>('/portal/what-we-buy'),
      farmerApi.get<Offer[]>('/portal/offers'),
    ])
      .then(([profile, buyable, mine]) => {
        if (cancelled) return
        setMe(profile)
        setBuy(buyable)
        setOffers(mine)
      })
      .catch((e) => !cancelled && setError(e?.message ?? 'Could not load your details.'))
      .finally(() => !cancelled && setLoading(false))
    return () => { cancelled = true }
  }, [])

  const firstName = me?.name.split(' ')[0] ?? ''
  const greeting = new Date().getHours() < 12 ? 'Habari' : 'Habari ya jioni'
  const live = offers.filter((o) => ['open', 'accepted', 'scheduled'].includes(o.status))
  const coming = live.find((o) => o.status === 'scheduled')

  // Best price on offer today, so the home screen leads with a reason to come back.
  const best = [...buy].sort((a, b) => Number(b.pricePerKg) - Number(a.pricePerKg))[0]

  return (
    <>
      <FarmerHeader />

      <div className="field-greet">
        <h1>{greeting}{firstName ? `, ${firstName}` : ''}</h1>
        <p>
          {new Date().toLocaleDateString('en-KE', { weekday: 'long', day: 'numeric', month: 'long' })}
          {me?.zone && <> · {me.zone}</>}
        </p>
      </div>

      <div className="field-body">
        {loading && <Panel><Loading rows={4} /></Panel>}
        {error && <Banner tone="crit" title="Could not load your details">{error}</Banner>}

        {me && (
          <>
            {coming ? (
              <div className="brand-card">
                <span className="micro">We are coming for</span>
                <div className="bc-title">
                  {coming.cropName}
                  <span>{num(coming.estimatedKg, 0)} kg</span>
                </div>
                <div className="bc-grid">
                  <div>
                    <b>{shortDate(coming.scheduledFor)}</b>
                    <i>Collection day</i>
                  </div>
                  <div>
                    <b className="num">{money(coming.indicativeValue, { compact: true })}</b>
                    <i>At today's price</i>
                  </div>
                </div>
                <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--on-brand-dim)', textAlign: 'center' }}>
                  Have it ready and weighed on our scale. You are paid the same day.
                </p>
              </div>
            ) : (
              <div className="brand-card">
                <span className="micro">Best price today</span>
                {best ? (
                  <>
                    <div className="bc-title">
                      {best.cropName}
                      <span>Grade {best.gradeCode}</span>
                    </div>
                    <div className="bc-hero num">{money(best.pricePerKg)} <span style={{ fontSize: 'var(--fs-sm)' }}>/ kg</span></div>
                  </>
                ) : (
                  <div className="bc-title">No prices published yet</div>
                )}
                <Link className="btn block" to="/f/post" style={{ background: '#fff', color: 'var(--brand-900)', borderColor: '#fff' }}>
                  Post a harvest
                </Link>
              </div>
            )}

            {live.length > 0 && (
              <Panel title="Your harvests" meta={`${live.length} waiting`}>
                <div className="panel-body" style={{ paddingTop: 4, paddingBottom: 4 }}>
                  {live.map((o) => {
                    const s = offerStatusLabel(o.status)
                    return (
                      <div key={o.id} className="stop-row" style={{ cursor: 'default' }}>
                        <span className="stop-who">
                          <b>{o.cropName} · {num(o.estimatedKg, 0)} kg</b>
                          <i>Ready {shortDate(o.readyFrom)}{o.scheduledFor ? ` · we come ${shortDate(o.scheduledFor)}` : ''}</i>
                        </span>
                        <span className="stop-amt">
                          <b>{money(o.indicativeValue, { currency: false })}</b>
                          <span><Pill tone={s.tone} dot={false}>{s.text}</Pill></span>
                        </span>
                      </div>
                    )
                  })}
                </div>
              </Panel>
            )}

            <Panel title="What we are buying" meta="today's prices">
              <div className="panel-body" style={{ paddingTop: 4, paddingBottom: 4 }}>
                {buy.length === 0 && <Empty title="No prices published yet" />}
                {buy.slice(0, 8).map((b) => (
                  <div key={`${b.cropId}-${b.gradeId}`} className="stop-row" style={{ cursor: 'default' }}>
                    <span className="stop-who">
                      <b>{b.cropName}{b.localName ? ` (${b.localName})` : ''}</b>
                      <i>Grade {b.gradeCode} — {b.specText}</i>
                    </span>
                    <span className="stop-amt">
                      <b>{money(b.pricePerKg, { currency: false })}</b>
                      <span>per kg</span>
                    </span>
                  </div>
                ))}
              </div>
            </Panel>

            <div className="mini-row">
              <StatMini label="Sold to us" value={`${num(me.totals.kg, 0)} kg`} note={`${me.totals.lots} collections`} />
              <StatMini label="Paid to you" value={money(me.totals.paid, { compact: true })} />
              {me.reliability?.fulfilmentPct != null && (
                <StatMini
                  label="Your estimates"
                  value={`${me.reliability.fulfilmentPct}%`}
                  note="of what you offer, delivered"
                />
              )}
            </div>

            {me.kycStatus !== 'verified' && (
              <Banner tone="warn" title="Your details are not verified yet">
                You can still post harvests. Payment is held until the agent has checked your ID and
                that the M-Pesa name matches yours.
              </Banner>
            )}

            {me.payoutAccount?.nameMatchState === 'mismatch' && (
              <Banner tone="crit" title="The M-Pesa name does not match yours">
                Payment cannot be sent until this is fixed. Speak to the agent who collects from you.
              </Banner>
            )}
          </>
        )}
      </div>
    </>
  )
}
